/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { MicrotaskAssignmentRecord, TaskRecord, WorkerRecord } from '@karya/core';
import { RenderAssignment } from './RenderAssignment';
import { RenderCompletedAssignment } from './RenderCompletedAssignment';
import React from 'react';
import { Box } from 'chakra-v3';

/**
 * Props for ShowAssignments component
 */
export interface RenderAllAssignmentsProps {
  activeWorker: WorkerRecord;
  assignments: MicrotaskAssignmentRecord[];
  task: TaskRecord;
  onComplete: () => void | Promise<void>;
  scrollToBottom?: () => void;
}

/**
 * A component that shows assignments for a given avatar.
 * @param props - Props for ShowAssignments component
 * @returns A component that shows assignments for a given avatar
 */
function RenderAllAssignments(props: RenderAllAssignmentsProps) {
  const sortedAssignments = props.assignments
    .slice(0)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Already completed assignments
  const completedAssignments = sortedAssignments.filter((a) =>
    ['COMPLETED', 'SUBMITTED', 'VERIFIED'].includes(a.status),
  );

  // Find the first assignment with status 'ASSIGNED'
  const currentAssignment = sortedAssignments.find((a) => a.status === 'ASSIGNED');

  return (
    <React.Fragment>
      <Box w="100%">
        {completedAssignments.map((assignment) => (
          <RenderCompletedAssignment key={assignment.id} assignment={assignment} />
        ))}
      </Box>
      <Box w="100%">
        {currentAssignment && (
          <RenderAssignment
            key={currentAssignment.id}
            assignment={currentAssignment}
            taskParams={props.task.params}
            spec={props.task.params.spec}
            worker={props.activeWorker}
            isActiveAssignment={true}
            onComplete={props.onComplete}
            scrollToBottom={props.scrollToBottom}
          />
        )}
      </Box>

      {!currentAssignment && completedAssignments.length === 0 && <div>No assignments available.</div>}
      {!currentAssignment && completedAssignments.length > 0 && <div>No active work remaining</div>}
    </React.Fragment>
  );
}

export default RenderAllAssignments;
